import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import {compose} from 'redux';
import queryString from 'query-string';
import moment from 'moment';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';

import FontAwesome from 'utils/FA';
import { titleize } from 'utils/strings';
import { formatCurrency, formatDate, getMetaContent } from 'utils';
import { duckify, filterDuck, singleDuck, dependentDuck } from 'utils/duckify';
import {
  billingAccountContacts, billingAccounts,
  billingCategoryGroups as billingCategoriesDuck,
  bulkAddTransactionJobs,
  fields as fieldsDuck,
  flashMessages,
  modals,
  users,
  customers as customersDuck,
} from 'ducks';

import ContactList from 'components/ContactList/ContactList';
import Filters from 'components/Filters/Filters';
import SummaryTotalsHeader from 'components/ContactList/SummaryTotalsHeader';

import withFiltering from 'hocs/withFiltering';
import withPagination from 'hocs/withPagination';

import queryBuilder from '../../utils/queryBuilder'
import withSchoolYear, {defaultBillingSchoolYear} from '../../hocs/withSchoolYear';
import withSorting from 'hocs/withSorting';
import './styles/AccountsListPage.scss';
import useBulkActions from "../../hooks/useBulkActions";
import BulkActions from "../ContactList/BulkActions/BulkActions";
import Edit from "../ContactList/BulkActions/Edit";
import AddCharge from "../ContactList/BulkActions/AddCharge";
import AddCredit from "../ContactList/BulkActions/AddCredit";
import CommunicationMenu from '../ContactList/BulkActions/CommunicationMenu'
import FlashRoot from "../FlashMessage/FlashRoot";
import LinkButton from "../LinkButton/LinkButton";
import {
  bulkCheckboxColumn,
  currencyColumn,
  dateColumn,
  defaultColumn,
  linkColumn
} from '../ContactList/ColumnDefinitions';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import ReportRunButtons from './ReportRunButtons';
import { pollJob } from '../../utils/jobPoller';
import history from '../../shared_history';
import ReviewSpinner from '../ContactRecord/Review/ReviewSpinner';

const customerDuck = singleDuck(customersDuck, 'current');

const AccountsListPage = ({
  fields,
  additionalFields,
  billingCategories,
  filters,
  onSort,
  setFilters,
  sort,
  addTransaction,
  showFlash,
  showModal,
  hideModal,
  dispatch,
  currentUser,
  allDucksReady
}) => {
  const [currentJobId, setCurrentJobId] = useState('');
  const [ready, setReady] = useState(false);
  const [report, setReport] = useState({});
  const [contacts, setContacts] = useState([]);
  const [reportLoaded, setReportLoaded] = useState(false);
  const [contactsSearch, setContactsSearch] = useState([]);
  const [pagedContacts, setPagedContacts] = useState([]);
  const [page, setPage] = useState(1);

  const {
    trackedItems,
    selectionModeCount,
    promptSelectAll,
    setPromptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    itemIsSelected,
    selectAllItems
  } = useBulkActions(contacts);

  const {
    date, rows = [], summary, count, totals, generated_at
  } = report;

  const columns = [
    bulkCheckboxColumn({
      value: 'id',
      promptSelectAll,
      togglePageTracked,
      selectionMode,
      itemIsSelected,
      toggleTrackedItem
    }),
    linkColumn({
      header: 'Name',
      sortName: 'display_name',
      value: (contact) => (contact.display_name),
      link: (contact) => `/contacts/${contact.id}/billing`
    }),
    defaultColumn({
      header: 'AutoPay',
      sortName: 'billing_autopay_enabled',
      type: 'icon',
      value: (contact, key) => (
        <td className='contact-list-react-type-icon' key={key}>
          {
            contact.billing_autopay_enabled === true
              ? <FontAwesome className='autopay-check green' name='check'/>
              : ''
          }
        </td>
      )
    }),
    defaultColumn({
      header: 'Payment Method',
      sortName: 'billing_payment_method',
      value: (contact) => (
        <td>
          {titleize(contact.payment_method)}
        </td>
      )
    }),
    dateColumn({
      header: 'Overdue Date',
      sortName: 'billing_overdue_at',
      value: (contact) => {
        const isToday = moment(new Date()).isSame(contact.billing_overdue_at, 'day');
        const daysAgo = moment(contact.billing_overdue_at).fromNow(true);

        const overdueDate = contact.billing_overdue_at ? (
          <span className='billing-overdue-date'>
            {formatDate(contact.billing_overdue_at)}
          </span>
        ) : null;

        const overdueDays = contact.billing_overdue_at && !isToday ? (
          <span className='billing-overdue-days'>
            {contact.billing_overdue_at && !isToday ? `(${daysAgo})` : null}
          </span>
        ) : null;

        return (
          <td className='contact-list-react-type-date'>
            {overdueDate}
            &nbsp;
            {overdueDays}
          </td>
        );
      }
    }),
    dateColumn({
      header: 'Next Date',
      sortName: 'next_account_billing_date',
      value: 'next_account_billing_date'
    }),
    currencyColumn({
      header: 'Next Amount',
      sortName: 'next_account_balance',
      value: 'next_account_balance'
    }),
    currencyColumn({
      header: 'Remaining Balance',
      sortName: 'total_account_balance',
      value: 'total_account_balance'
    }),
    currencyColumn({
      header: 'Current Balance',
      sortName: 'current_account_balance',
      value: (contact) => (
        <td className='contact-list-react-type-currency'>
          {
            contact.billing_overdue_at ? (
              <span>
                <FontAwesome icon={faExclamationTriangle} className='red' postNbsp/>
              </span>
            ) : null
          }

          <span>
            {formatCurrency(contact.current_account_balance)}
          </span>
        </td>
      )
    }),
    defaultColumn({
      header: 'Autopay Billing Days',
      value: 'autopay_billing_days',
      sortName: 'autopay_billing_days',
      columnClassNames: 'contact-list-react-type-number'
    }),
    defaultColumn({
      header: 'Actions',
      columnClassNames: 'account_actions',
      value: (contact) => (
        <td className='contact-list-react-type-currency'>
          {
            contact.is_account_active  ?
              <span className='deactivate_account_button' onClick={() => activateAccount(contact.billing_account_id, false)}>
                Make Inactive
              </span>
              : <span className='activate_account_button' onClick={() => activateAccount(contact.billing_account_id, true)}>
                  Make Active
               </span>
          }
        </td>
      )
    })
  ];

  const totalPages = Math.ceil(contacts.length / report.per_page);

  const summaryColumns = [
    {name: 'next_account_balance', publicName: 'Next Amount'},
    {name: 'total_account_balance', publicName: 'Remaining Amount'},
    {name: 'current_account_balance', publicName: 'Current Amount'}
  ];

  const filterFields = [
    {
      solr_prompt: 'is_account_active',
      display_name: 'Billing Account Active',
      options: [['Yes', 'true'], ['No', 'false']]
    }
  ].concat(fields.concat(additionalFields));

  const defaultSelected = {
    is_account_active: ['true']
  };

  const activateAccount = (billingAccountId, isActive) => {
    dispatch(billingAccounts.actions.update({ id: billingAccountId, active: isActive })).then((response) => {
      const isActiveMessage = isActive ? 'reactivated' : 'deactivated';
      const message = `This account is being ${isActiveMessage} in the background. Please wait and check back later`;
      showFlash({ message });
    }).catch((response) => {
      if (response.billing_account !== undefined && response.billing_account.errors !== undefined
        && response.billing_account.errors.active !== undefined &&
        response.billing_account.errors.active[0] !== undefined) {
        showFlash({ type: 'danger', message: response.billing_account.errors.active[0] });
      } else {
        Utility.displayFlashMessages({ error: 'Sorry, something went wrong.' });
      }
    });
  };

  const runReport = () => {
    getNewReport();
  }

  // after getting the data back,
  const constructReport = (json, search) => {
    setCurrentJobId(search.job_id);
    setContactsSearch(json.search);
    setReport(json);

    let sort;
    if (search.sort === undefined) {
      sort = '{"column":"display_name", "order":"asc"}'
    } else {
      sort = search.sort
    }

    sortReport(sort, json.rows);
    setReportLoaded(true);
  };

  const sortReport = (searchSort, rows) => {
    if (rows === undefined) {
      rows = contacts;
    }

    if (searchSort === undefined) {
      searchSort = '{"column":"display_name", "order":"asc"}';
    }

    const sortJson = JSON.parse(searchSort) || {};
    const direction = sortJson.order;
    const columnKey = sortJson.column;

    const columnDef = columns.find(c => c.sortName === columnKey);
    const columnType = columnDef.type;
    const columnValue = columnDef.value;

    const newSortedRows = rows.sort((a, b) => {
      if (!columnKey) return 0;
      let valA = a[columnKey];
      let valB = b[columnKey];
      if (valA == null) valA = "";
      if (valB == null) valB = "";
      if (columnType === 'currency') {
        valA = parseFloat(valA.replace(/\D/g, ''));
        valB = parseFloat(valB.replace(/\D/g, ''));
      } else if (columnType === 'date' || columnValue === 'autopay_billing_days') {
        valA = new Date(valA);
        valB = new Date(valB);
      } else if (columnType === 'icon') {
        valA = valA ? 1 : 0;
        valB = valB ? 1 : 0;
      }
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (!isNaN(valA) && !isNaN(valB)) {
        return (parseFloat(valA) - parseFloat(valB)) * (direction === "asc" ? 1 : -1);
      }
      return (valA < valB ? -1 : 1) * (direction === "asc" ? 1 : -1);
    });
    setContacts(newSortedRows);
    updatePagedContacts(newSortedRows);
  };

  const fetchReport = (jobId) => {
    fetch(`/api/billing_account_contacts/${jobId}`, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.json();
      })
      .then(json => {
        const currentSearch = queryString.parse(location.search);
        constructReport(json, currentSearch);
        setFilters(json.job_filters);
        setReady(true);
      })
      .catch(() => {
        dispatch(modals.actions.show('UnexpectedError'));
        setReady(false);
      });
  }

  const isInitialLoad = useRef(true);
  // Since we are hiding the run buttons, this runs the report as soon as the page loads
  useEffect(() => {
    const currentSearch = queryString.parse(location.search);

    if (!isInitialLoad.current) return;
    if (currentSearch.job_id) {
      // If job_id exists, fetch the existing report
      fetchReport(currentSearch.job_id);
    } else {
      // If no job_id, create a new report
      getNewReport();
    }
  }, []);

  const updatePagedContacts = (updatedContacts) => {
    const per_page = report.per_page || 25;
    const start = (page - 1) * per_page;
    const end = start + per_page;

    setPagedContacts(updatedContacts.slice(start, end));
  };

  // when we update the data or the page, we need to update the pagedContacts to change which ones are on the page
  useEffect(() => {
    updatePagedContacts(contacts);
  }, [contacts, report, page]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  }

  // when we get the report back, this will trigger the building of the report
  const prevSearchRef = useRef(queryString.parse(location.search));

  useEffect(
    () => {
      const currentSearch = queryString.parse(location.search);

      // Check if the only change is the `sort` variable
      const isOnlySortChanged =
        Object.keys(currentSearch).every(key =>
          key === 'sort' || key === 'page' || currentSearch[key] === prevSearchRef.current[key]
        );

      const sortValue = Object.keys(currentSearch).includes('sort') ? currentSearch['sort'] : null;

      setReady(false);
      if (currentSearch.job_id) {
        fetchReport(currentSearch.job_id);
      }
      prevSearchRef.current = currentSearch; // Update the ref after processing

    },

  [location.search, allDucksReady]
  );

  const handleSubmitFilters = (filters) => {
    getNewReport(filters, true);
  };

  const getNewReport = (newFilters = {}, force = false) => {
    if (!ready || force) {
      const query = queryString.parse(location.search);
      let searchParams = {};

      if (Object.keys(newFilters).length !== 0) searchParams.filters = JSON.stringify(newFilters);
      const reqSearchString = `?${queryString.stringify(searchParams)}`;

      setReady(true);
      setReportLoaded(false);

      const token = getMetaContent('csrf-token');

      fetch(`/api/billing_account_contacts${reqSearchString}&force=${force}`, {
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', 'X-CSRF-Token': token},
        method: 'POST'
      })
        .then(response => response.json())
        .then(jobInfo => {
          if (jobInfo.error) {
            throw new Error(jobInfo.error)
          } else {
            return pollJob(jobInfo);
          }
        })
        .then(response => {
          const newJobId = response.billing_account_contacts_report_job.id;
          const search = { job_id: newJobId };
          const resSearchString = `?${queryString.stringify(search)}`;

          history.push({
            ...location,
            search: resSearchString
          });
        })
        .catch((error) => {
          if (error && error.message) {
            dispatch(modals.actions.show('UnexpectedError', {message: error.message}));
          } else {
            dispatch(modals.actions.show('UnexpectedError'));
          }
          setReady(false);
        });
    }
  };

  return (
    <div className='billing-students-list'>
      <FlashRoot />
      <div className='contact-list-react-page-header'>
        <div className='contact-list-react-page-title accounts-space-between'>

          <h2 className='inline'>
            Accounts
          </h2>

          <BulkActions
            search={contactsSearch}
            contactTotal={contacts.length}
            filterType={selectionMode}
            filterContactIds={trackedItems}
            showFlash={(props) => showFlash(props)}
            showModal={(name, props) => showModal(name, props)}
            hideModal={() => hideModal()}
          >
            <BulkAction disabled={!reportLoaded} title='Export' icon={faSignOut}>
              <BulkExportAction
                type='AccountReportJob'
                location={location}
                url='/api/billing_account_contacts/export.csv'
              />
              <BulkExportAction
                title='Export to PDF'
                type='AccountReportJob'
                location={location}
                url='/api/billing_account_contacts/export.pdf'
              />
            </BulkAction>
            <CommunicationMenu disabled={!reportLoaded || selectionModeCount() === 0} active={currentUser.communications_permitted} selectableContactTypes={['selected']} />
            <Edit disabled={!reportLoaded || !billingCategories.ready || selectionModeCount() === 0}>
              <AddCharge
                addTransaction={addTransaction}
                billingCategories={billingCategories}
                count={selectionModeCount()}
                jobType={'account'}
              />
              <AddCredit
                addTransaction={addTransaction}
                billingCategories={billingCategories}
                count={selectionModeCount()}
                jobType={'account'}
              />
            </Edit>
          </BulkActions>
        </div>

        { !reportLoaded && (
          <>
            <FontAwesome name='spinner' spin />
            &nbsp;Generating your results.  They will appear below, but can also be viewed on the&nbsp;
            <Link to='/setup/user_jobs'>User Jobs Page</Link>
          </>
        )}

        <div>

          {reportLoaded && (
            <SummaryTotalsHeader data={report.summary} columns={summaryColumns}/>
          )}

          <div className='accounts-space-between'>
            <div>
              {promptSelectAll && (
                <div className={'contact-list-react-prompt-select-all'}>
                  <span>You have selected <strong>{selectionModeCount()}</strong> contacts.</span>
                  &nbsp;
                  <LinkButton
                    onClick={selectAllItems}
                    content={<span>Select all <strong>{contacts.length}</strong> accounts across all pages.</span>}
                  />
                </div>
              )}

              {(filterFields.length) > 0 && (
                <Filters
                  fields={filterFields}
                  selectCount={selectionModeCount()}
                  count={contacts.length}
                  selected={{...defaultSelected, ...filters}}
                  submitFilters={(filters) => handleSubmitFilters(filters)}
                  itemsName='Accounts'
                />
              )}
            </div>

            {totalPages > 1 && (
              <div className='contact-list-react-pagination accounts-pagination'>
                <div className='pagination pagination-right'>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    hideFirstAndLastPageLinks
                    onChange={(newPage) => {
                      onPageChange(newPage);
                      setPromptSelectAll(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {reportLoaded && (
            <ContactList
              columns={columns}
              contacts={pagedContacts}
              onSort={onSort}
              sort={sort}
            />
          )}

        </div>
      </div>
    </div>
  );
};

AccountsListPage.propTypes = {
  /** The default fields that are available to filter by */
  fields: PropTypes.arrayOf(PropTypes.object),

  /** Additional fields that are available to filter by */
  additionalFields: PropTypes.arrayOf(PropTypes.object),

  /** The filters selected for this page */
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Used to generate a link to sort by a given column */
  onSort: PropTypes.func,

  /** Sets filters to a given filterset */
  setFilters: PropTypes.func,

  /** The sort selected for this page */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  }),

  /** Hides all modals */
  hideModal: PropTypes.func,
  /** Shows a flash message */
  showFlash: PropTypes.func,

  /** Shows a modal */
  showModal: PropTypes.func,
  /** Adds a transaction */
  addTransaction: PropTypes.func,

  /** add React dispatch */
  dispatch: PropTypes.func,

  allDucksReady: PropTypes.bool
};

AccountsListPage.defaultProps = {
  contacts: [],
  fields: [],
  additionalFields: [],
  filters: {},
  sort: {},

  onSort: () => false,
  setFilters: () => false,
  setPage: () => false,
  showFlash: () => false,
  showModal: () => false,
  hideModal: () => false,
  addTransaction: () => false,
  dispatch: () => false,
};

const mapDucksToProps = ({location, sortString}) => {
  const query = queryString.parse(location.search);
  query.sort = sortString;

  return {
    billingCategories: filterDuck(billingCategoriesDuck, {active: true, non_contact_only: true}),
    fields: filterDuck(fieldsDuck, {
      solr_prompt: ['billing_autopay_enabled', 'billing_overdue', 'billing_payment_method']
    }),
    additionalFields: filterDuck(fieldsDuck, {for: 'billing'}),
    currentUser: singleDuck(users, 'current'),
    customer: customerDuck,
  };
};

const mapDispatchToProps = (dispatch) => ({
  showModal: (name, props) => dispatch(modals.actions.show(name, props)),
  addTransaction: (transaction) => dispatch(bulkAddTransactionJobs.actions.create(transaction)),
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  hideModal: () => dispatch(modals.actions.hide())
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({column: 'display_name', order: 'desc'}),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(defaultBillingSchoolYear)
)(AccountsListPage);
